import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAccount } from '../../../contexts/AccountContext';
import { usePricing } from '../../../contexts/PricingContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useTeamLimits } from '../../../hooks/useTeamLimits';
import { apiService } from '../../../services/api';
import { PERMISSIONS } from '../../../shared/constants/permissions';
import type { Tender } from '../../../types/tender';
import { EstimatedCostChip } from '../../common/EstimatedCostChip';
import type { LoadingStates } from '../types';
import {
  calculateResetCooldown,
  extractProgressFromStatus,
  formatGenerationStatusText,
  getDocTimestampFromLink,
  getLoadingKey,
  isServiceProcessingStatus,
} from '../utils';

interface AnalysisActionCardProps {
  tender: Tender;
  loadingStates: LoadingStates;
  onRegenerate: (docType: string) => void;
  onDownload: (gsURI: string, fileTypeIdentifier: string, defaultFileName: string) => void;
  onReset: () => void;
  onUpdateStatus: (docType: string, status: string) => void;
}

const AnalysisActionCard: React.FC<AnalysisActionCardProps> = ({
  tender,
  loadingStates,
  onRegenerate,
  onDownload,
  onReset,
  onUpdateStatus,
}) => {
  const theme = useTheme();
  const { account } = useAccount();
  const { pricingSettings } = usePricing();
  const { showSnackbar } = useSnackbar();
  const [resetCooldown, setResetCooldown] = useState<number>(0);
  const [errorReported, setErrorReported] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const [lastErrorStatus, setLastErrorStatus] = useState('');
  const [confirmGenOpen, setConfirmGenOpen] = useState(false);

  const status = tender.alignmentAnalysis?.status || '';
  const statusLower = status.toLowerCase();
  const isError =
    (statusLower.includes('error') || statusLower.includes('failed')) &&
    !statusLower.includes('complete') &&
    !statusLower.includes('closed');

  const [maxProgress, setMaxProgress] = useState(0);
  useEffect(() => {
    const currentProgress =
      extractProgressFromStatus(status, tender.alignmentAnalysis?.progress) ?? 0;
    const activeProcessing = isServiceProcessingStatus(status);

    if (!activeProcessing) {
      setMaxProgress(0);
    } else {
      setMaxProgress((prev) => Math.max(prev, currentProgress));
    }
  }, [status, tender.alignmentAnalysis?.progress]);

  useEffect(() => {
    if (status !== lastErrorStatus) {
      setLastErrorStatus(status);
      if (!isError) {
        setErrorDismissed(false);
        setErrorReported(false);
      }
    }
  }, [status, isError, lastErrorStatus]);

  const handleReportError = async () => {
    try {
      await apiService.reportError({
        tenderId: tender.id || 'unknown',
        error: status,
        component: 'Alignment Analysis',
      });
      setErrorReported(true);
      setErrorDismissed(true);
      showSnackbar('Error reported. Thank you.', 'success');
      onUpdateStatus('alignmentAnalysis', 'Error Report Complete');
    } catch (_e) {
      showSnackbar('Failed to report error.', 'error');
    }
  };

  // ... (keep existing logic for buttonState and cooldown)

  const loadingKey = getLoadingKey(tender.id, 'regenerate', 'alignmentAnalysis');
  const isLocalLoading = !!loadingStates[loadingKey];

  const getButtonState = () => {
    const status = tender.alignmentAnalysis?.status || '';
    const isProcessing =
      isLocalLoading ||
      (status &&
        !status.toLowerCase().includes('complete') &&
        !status.toLowerCase().includes('error') &&
        !status.toLowerCase().includes('idle'));
    const exists = !!tender.analysisFileLink;

    if (isProcessing) {
      return { text: '', showSpinner: true };
    }
    if (!exists) {
      return { text: 'Generate Analysis', showSpinner: false };
    }
    return { text: 'Regenerate', showSpinner: false };
  };

  const buttonState = getButtonState();

  useEffect(() => {
    const updateCooldown = () => {
      setResetCooldown(calculateResetCooldown(tender.alignmentAnalysis, 30));
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 60000);
    return () => clearInterval(interval);
  }, [tender.alignmentAnalysis]);
  const getRegenButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.greyContrast : theme.palette.greyLight,
    color: enabled ? theme.palette.greyLight : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: '36px',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': enabled
      ? {
          backgroundColor: theme.palette.bidlogiq?.brick || '#b73f2a',
          color: '#fff',
          boxShadow: theme.shadows[4],
        }
      : {
          backgroundColor: theme.palette.greyLight,
          color: theme.palette.text.primary,
          boxShadow: theme.shadows[4],
        },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq?.orange}`,
    },
    width: '100%',
    mb: 1,
  });

  const getDownloadButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.bidlogiq.brick : theme.palette.greyLight,
    color: enabled ? theme.palette.bidlogiq.white : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: 'auto',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': {
      backgroundColor: enabled ? theme.palette.bidlogiq.rust : theme.palette.greyLight,
      boxShadow: theme.shadows[4],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq.orange}`,
    },
    width: '100%',
    justifyContent: 'center',
    '& .MuiButton-label': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      lineHeight: 1.2,
    },
  });

  const isProcessing = buttonState.showSpinner;

  const { hasPermission } = useAccount();
  const canGenerate = hasPermission(PERMISSIONS.REPORT_CREATE);

  const { checkLimit } = useTeamLimits();
  const usedCount = tender.reportCount || 0;
  const { remaining, isLimitReached, isUnlimited } = checkLimit('reports', usedCount);

  const getRegenerateButtonText = () => {
    if (!canGenerate) {
      return 'Permission Required';
    }

    if (isProcessing) {
      return formatGenerationStatusText(tender.alignmentAnalysis?.status, isLocalLoading);
    }

    if (isLimitReached) {
      return `Regenerate Analysis (Limit Reached)`;
    }

    if (isUnlimited) {
      return `Regenerate Analysis`;
    }

    return `Regenerate Analysis (${remaining} remaining)`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {isError && !errorDismissed && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            px: 2,
            borderRadius: 1,
            bgcolor: (theme) => theme.palette.error.light,
            color: (theme) => theme.palette.error.contrastText,
            border: (theme) => `1px solid ${theme.palette.error.main}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            <ErrorIcon fontSize="medium" />
            <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
              <span style={{ fontWeight: 'bold' }}>Generation Failed:</span> {status} - Please
              report to admin.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleReportError}
              disabled={errorReported}
              sx={{
                whiteSpace: 'nowrap',
                bgcolor: 'white',
                color: 'error.main',
                borderColor: 'error.main',
                py: 0.5,
                px: 2,
                minHeight: '32px',
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {errorReported ? 'Report Sent' : 'Notify Admin'}
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setErrorDismissed(true);
                onUpdateStatus('alignmentAnalysis', 'Error Closed');
              }}
              sx={{ color: 'inherit', p: 0.5 }}
            >
              {' '}
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Button
                onClick={() => setConfirmGenOpen(true)}
                disabled={!canGenerate || buttonState.showSpinner || isLimitReached}
                sx={getRegenButtonStyle(
                  theme,
                  canGenerate && !buttonState.showSpinner && !isLimitReached
                )}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  <Box /> {/* Left spacer for centering */}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                  >
                    {buttonState.showSpinner ? (
                      <CircularProgress
                        size={16}
                        variant="indeterminate"
                        sx={{ color: theme.palette.bidlogiq.orange }}
                      />
                    ) : (
                      <SyncIcon />
                    )}
                    <Box>{getRegenerateButtonText()}</Box>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: '35px' }}>
                    {buttonState.showSpinner &&
                    extractProgressFromStatus(status, tender.alignmentAnalysis?.progress) !==
                      undefined
                      ? `${maxProgress}%`
                      : ''}
                  </Box>
                </Box>
              </Button>
            </Box>
            {!isLimitReached && (
              <Box>
                <Tooltip
                  title="Exact billing depends on the length and complexity of the tender documents and provided context."
                  slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                >
                  <EstimatedCostChip
                    estimate={
                      pricingSettings?.estimates?.capability_analysis || 'Est. $20.00 – $60.00'
                    }
                  />
                </Tooltip>
              </Box>
            )}
          </Box>

          <Button
            startIcon={
              loadingStates[getLoadingKey(tender.id, 'download', 'alignment.docx')] ? (
                <CircularProgress size={18} sx={{ color: 'bidlogiq.orange' }} />
              ) : (
                <DownloadIcon />
              )
            }
            onClick={() =>
              onDownload(
                tender.analysisFileLink || '',
                'alignment.docx',
                tender.analysisFileLink
                  ? tender.analysisFileLink.split('/').pop()?.split('?')[0] || 'alignment.docx'
                  : 'alignment.docx'
              )
            }
            disabled={
              !tender.analysisFileLink ||
              !!loadingStates[getLoadingKey(tender.id, 'download', 'alignment.docx')]
            }
            sx={getDownloadButtonStyle(theme, !!tender.analysisFileLink)}
          >
            <Box
              sx={{
                lineHeight: 1.2,
                fontWeight: 600,
                textAlign: 'center',
                fontSize: '1rem',
              }}
            >
              Download analysis
              {(tender.analysisFileLink || tender.alignmentAnalysis?.startedAt) && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    opacity: 0.9,
                    textTransform: 'none',
                  }}
                >
                  {`, ${getDocTimestampFromLink(tender.analysisFileLink, tender.alignmentAnalysis)}`}
                </Box>
              )}
              <Box component="span" sx={{ textTransform: 'none' }}>
                {' (docx)'}
              </Box>
            </Box>
          </Button>

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {(() => {
              const isFeatureEnabled = account?.clientSettings?.generateCapabilityPitchDeck;

              if (!isFeatureEnabled) {
                return (
                  <Button
                    disabled
                    sx={{
                      ...getDownloadButtonStyle(theme, false),
                      flex: 1,
                      px: 1,
                      minHeight: '36px',
                    }}
                  >
                    <Box
                      sx={{
                        lineHeight: 1.2,
                        fontWeight: 600,
                        textAlign: 'center',
                        fontSize: '1rem',
                      }}
                    >
                      Go/No-Go deck (disabled in settings)
                    </Box>
                  </Button>
                );
              }

              let link = '';
              let format = '';
              let fileKey = '';
              const outputTypes = account?.clientSettings?.capabilityPitchOutputTypes || {
                pptx: true,
                html: false,
                pdf: false,
              };

              // 1. Prioritize links that match the user's CURRENT requested output settings
              if (outputTypes.html && tender.capabilityPitchHtmlLink) {
                link = tender.capabilityPitchHtmlLink;
                format = 'html';
                fileKey = 'pitch.html';
              } else if (outputTypes.pdf && tender.capabilityPitchPdfLink) {
                link = tender.capabilityPitchPdfLink;
                format = 'pdf';
                fileKey = 'pitch.pdf';
              } else if (outputTypes.pptx && tender.capabilityPitchPptxLink) {
                link = tender.capabilityPitchPptxLink;
                format = 'pptx';
                fileKey = 'pitch.pptx';
              }
              // 2. Fallback to any available link if the settings changed after generation
              else if (tender.capabilityPitchPptxLink) {
                link = tender.capabilityPitchPptxLink;
                format = 'pptx';
                fileKey = 'pitch.pptx';
              } else if (tender.capabilityPitchHtmlLink) {
                link = tender.capabilityPitchHtmlLink;
                format = 'html';
                fileKey = 'pitch.html';
              } else if (tender.capabilityPitchPdfLink) {
                link = tender.capabilityPitchPdfLink;
                format = 'pdf';
                fileKey = 'pitch.pdf';
              }
              // 3. Fallback before generation has occurred to show the intended format
              else {
                if (outputTypes.html) {
                  format = 'html';
                  fileKey = 'pitch.html';
                } else if (outputTypes.pdf) {
                  format = 'pdf';
                  fileKey = 'pitch.pdf';
                } else {
                  format = 'pptx';
                  fileKey = 'pitch.pptx';
                }
              }

              const isGenerating = tender.capabilityPitchStatus === 'Generating...';
              const hasError = tender.capabilityPitchStatus === 'Error';

              return (
                <Button
                  startIcon={
                    loadingStates[getLoadingKey(tender.id, 'download', fileKey)] || isGenerating ? (
                      <CircularProgress size={18} sx={{ color: 'bidlogiq.orange' }} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() =>
                    onDownload(link, fileKey, link.split('/').pop()?.split('?')[0] || fileKey)
                  }
                  disabled={
                    !link ||
                    !!loadingStates[getLoadingKey(tender.id, 'download', fileKey)] ||
                    isGenerating
                  }
                  sx={{
                    ...getDownloadButtonStyle(theme, !!link && !isGenerating),
                    flex: 1,
                    px: 1,
                    minHeight: '36px',
                  }}
                >
                  <Box
                    sx={{
                      lineHeight: 1.2,
                      fontWeight: 600,
                      textAlign: 'center',
                      fontSize: '1rem',
                    }}
                  >
                    Download pitch
                    {isGenerating ? (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 400,
                          opacity: 0.9,
                          textTransform: 'none',
                        }}
                      >
                        {`, processing`}
                      </Box>
                    ) : hasError ? (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 400,
                          opacity: 0.9,
                          textTransform: 'none',
                          color: 'error.main',
                        }}
                      >
                        {`, error`}
                      </Box>
                    ) : link || tender.capabilityPitchStartedAt ? (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 400,
                          opacity: 0.9,
                          textTransform: 'none',
                        }}
                      >
                        {`, ${getDocTimestampFromLink(link, tender.capabilityPitchStartedAt || tender.alignmentAnalysis?.startedAt ? { lastUpdated: (tender.capabilityPitchStartedAt || tender.alignmentAnalysis?.startedAt) as string | Date } : {})}`}
                      </Box>
                    ) : (
                      tender.alignmentAnalysis?.startedAt && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 400,
                            opacity: 0.9,
                            textTransform: 'none',
                          }}
                        >
                          {`, pending`}
                        </Box>
                      )
                    )}
                    <Box component="span" sx={{ textTransform: 'none' }}>
                      {` (${format})`}
                    </Box>
                  </Box>
                </Button>
              );
            })()}
          </Box>

          {tender.alignmentAnalysis?.status && (
            <Box
              sx={{
                mt: 1,

                display: 'flex',

                alignItems: 'center',

                gap: 2,

                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1, fontSize: '0.875rem' }}
              >
                Generating a report can take up to 30 minutes. Press the reset button if it is
                stuck.
              </Typography>

              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={onReset}
                disabled={resetCooldown > 0}
                sx={{
                  borderColor: 'text.error',

                  color: 'text.error',

                  whiteSpace: 'nowrap',
                }}
              >
                {resetCooldown > 0 ? `Reset (${resetCooldown}m)` : 'Reset'}
              </Button>
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              height: '100%',

              display: 'flex',

              flexDirection: 'column',

              alignItems: 'center',

              justifyContent: 'center',

              pl: { md: 2 },

              borderLeft: { md: `1px solid ${theme.palette.divider}` },
            }}
          >
            {tender.score !== undefined && tender.score !== null ? (
              <>
                <Typography
                  variant="h2"
                  sx={{ fontWeight: 800, color: theme.palette.bidlogiq.brick }}
                >
                  {tender.score}
                </Typography>

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '1rem' }}
                >
                  Score
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No Score
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmGenOpen}
        onClose={() => setConfirmGenOpen(false)}
        aria-labelledby="confirm-gen-dialog-title"
        aria-describedby="confirm-gen-dialog-description"
      >
        <DialogTitle id="confirm-gen-dialog-title">Confirm Capability Analysis</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-gen-dialog-description">
            Generating a new analysis will overwrite your current scores, strengths, and weaknesses.
            <br />
            <br />
            Do you wish to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGenOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmGenOpen(false);
              onRegenerate('alignmentAnalysis');
            }}
            color="error"
            variant="contained"
            autoFocus
          >
            Yes, Generate Analysis
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default AnalysisActionCard;
